import React, { useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
} from "chart.js";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip
);

interface BarChartProps {
  data: number[];
  labels: string[];
  labelColor: string[];
}

const HorizontalBarChart: React.FC<BarChartProps> = ({
  data,
  labels,
  labelColor,
}) => {
  const [open, setOpen] = useState(false);

  const modifiedLabels = labels.map((label) =>
    label.length > 10 ? `${label.slice(0, 10)}...` : label
  );

  const chartData = {
    labels: modifiedLabels,
    datasets: [
      {
        label: "",
        data: data,
        borderColor: "#23245f",
        backgroundColor: `${labelColor[0]}33`,
        pointBackgroundColor: labelColor,
        pointBorderColor: labelColor,
        pointBorderWidth: 2,
        tension: 0.4,
      },
    ],
  };

  const barChartData = {
    labels,
    datasets: [
      {
        label: "Data",
        data,
        backgroundColor: labelColor,
        borderColor: labelColor.map((color) => `${color}99`),
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems: any) => {
            const index = tooltipItems[0].dataIndex;
            return modifiedLabels[index];
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
          autoSkip: false,
          padding: 10,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          display: true,
        },
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          display: true,
          autoSkip: false,
          padding: 10,
          callback: function (value: any) {
            const label = labels[value];
            return label?.length > 11 ? `${label.slice(0, 11)}...` : label;
          },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        grid: {
          display: true,
        },
      },
    },
  };

  const handleClicked = () => {
    setOpen(true);
    localStorage.setItem("popupOpen", true.toString());
  };

  const handleRenameModelClose = () => {
    setOpen(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  return (
    <div
      style={{
        height: "100px",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Line
        data={chartData}
        options={options}
        onClick={handleClicked}
        className="charting"
      />
      <Dialog
        open={open}
        onClose={handleRenameModelClose}
        aria-labelledby="rename-model-dialog"
        aria-describedby="rename-model-description"
        PaperProps={{
          style: {
            boxShadow: "none",
            width: "90%", // Set width to 90% of the screen
            maxHeight: "90vh", // Set height to 90% of the viewport height
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "#2121210f",
              backdropFilter: "blur(5px)",
            },
          },
        }}
        BackdropProps={{
          style: {
            opacity: 0.3,
          },
        }}
      >
        <DialogTitle id="rename-model-dialog">
          Labels Chart
          {/* Top-right corner content */}
          <IconButton
            aria-label="close"
            onClick={handleRenameModelClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingBottom: "5px",
            paddingTop: "5px!important",
          }}
        >
          <div
            style={{
              marginTop: "10px",
              maxHeight: "80px", // Restricts the height of the container
              overflowY: "auto", // Enables vertical scrolling if content exceeds max height
              padding: "10px", // Adds padding for better spacing
              borderRadius: "8px", // Rounds the corners
              border: "1px solid #ccc", // Optional: Adds a border for a clear visual boundary
            }}
            id="labelProjectPopup"
          >
            <ul
              style={{
                padding: 0,
                margin: 0,
                listStyle: "none",
                fontSize: "14px",
              }}
            >
              {labels.map((label, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: labelColor[index % labelColor.length],
                      marginLeft: "10px",
                      borderRadius: "50%",
                    }}
                  ></div>
                  <span
                    style={{
                      color: "black",
                      marginLeft: "10px",
                    }}
                  >
                    {`${label} - ${data[index]}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div
          id="performanceChat"
            style={{
              height: "400px", // Increased height of the bar chart
              width: "100%",
            }}
          >
            <Bar data={barChartData} options={barOptions} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HorizontalBarChart;
